import { Box, Button, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { UpperTitle } from 'components/atoms';
import { useDarkMode } from 'hooks';
import { FC, ReactNode, useRef, useState } from 'react';
import Link from 'next/link';
import Carousel from 'react-multi-carousel';
import { Arrows } from 'components/structures/main/Slide';
import 'react-multi-carousel/lib/styles.css';

interface SlideProps {
  text: { title: string; description: string };
  button: { label: string; linkTo: string };
  icon: ReactNode;
}

interface SliderProps {
  sliderList: Array<SlideProps>;
}

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    marginBottom: 0,
    marginTop: theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(24),
      marginTop: theme.spacing(16),
    },
  },
  link: {
    color: (isDarkMode) => (isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark),
    fontSize: 16,
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
    },
  },
  boxWrapper: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 3),
    },
  },
  titleWrapper: {
    marginBottom: theme.spacing(6),
  },
  arrowsSection: {
    marginBottom: theme.spacing(11),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      marginBottom: theme.spacing(19),
    },
  },
  description: {
    margin: theme.spacing(3, 0, 6),
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(6),
  },
  text: {
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
    },
  },
}));

const IndustriesSlider: FC<SliderProps> = ({ sliderList }) => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useDarkMode();
  const classes = useStyles(isDarkMode);

  const prev = () => {
    if (!loading) {
      ref.current.previous();
      if (slide === 0) {
        setSlide(sliderList.length - 1);
      } else {
        setSlide(slide - 1);
      }
    }
  };

  const next = () => {
    if (!loading) {
      console.log('ref.current: ', ref.current);
      ref.current.next();
      if (slide >= sliderList.length - 1) {
        setSlide(0);
      } else {
        setSlide(slide + 1);
      }
    }
  };

  const goToSlide = (index: number) => {
    if (!loading) {
      ref.current.goToSlide(index + 2);
      setSlide(index);
    }
  };

  return (
    <Box className={classes.section}>
      <Container maxWidth={false} fixed>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={10} className={classes.titleWrapper}>
            <UpperTitle text="Our areas of expertise" />
            <Typography variant="h2">Industries we serve</Typography>
          </Grid>
          <Grid item xs={12} lg={10}>
            {sliderList.length > 1 && (
              <Arrows
                className={classes.arrowsSection}
                labels={sliderList.map(({ text: { title } }) => title)}
                onClickIndicator={goToSlide}
                totalSlides={Array.from(sliderList.keys())}
                currentSlide={slide}
                next={next}
                prev={prev}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Carousel
              ref={ref}
              responsive={{
                mobile: {
                  breakpoint: {
                    max: 960,
                    min: 0,
                  },
                  items: 1,
                },
                tablet: {
                  breakpoint: {
                    max: 5000,
                    min: 100,
                  },
                  items: 1,
                },
              }}
              beforeChange={() => setLoading(true)}
              afterChange={() => setLoading(false)}
              additionalTransfrom={0}
              minimumTouchDrag={80}
              arrows={false}
              swipeable={true}
              ssr={true}
              draggable={false}
              keyBoardControl={false}
              slidesToSlide={1}
              infinite
              itemClass={classes.boxWrapper}
            >
              {sliderList.map(({ icon, button, text }, index) => (
                <Grid justifyContent="center" container key={'item-' + index}>
                  <Grid style={{ paddingBottom: 30 }} item lg={10} xs={12}>
                    <Grid container>
                      <Grid className={classes.icon} item md={6} sm={12} xs={12}>
                        {icon}
                      </Grid>
                      <Grid item md={6} sm={12} xs={12} className={classes.text}>
                        <Typography variant="h3">{text.title}</Typography>
                        <Typography className={classes.description}>{text.description}</Typography>
                        <Link href={button.linkTo} passHref>
                          <Button variant="contained" color="primary">
                            {button.label}
                          </Button>
                        </Link>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Carousel>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default IndustriesSlider;
